import React from "react";

// Content for /product-release/rev36
//
// This is a static page — just write the release content directly as JSX below.
// Reuse the "product-release__*" classes (see ../product-release.css) to keep
// consistent spacing/typography, or add your own markup as needed.
//
// Compiled from every commit between the rev35 and rev36 branches of workflow-api and workflow-web.

const Rev36 = () => {
	return (
		<>
			<header className="product-release__header">
				<img className="product-release__logo" src="/assets/srj-black-logo.svg" alt="SRJ" />
				{/* TODO: Add release date, e.g. <span className="product-release__date">November 10, 2026</span> */}
				<h1 className="product-release__title">🚀 What's new in Workflow (rev36)</h1>
				<p className="product-release__summary">
					This release brings Playbooks (AI checks on a client's books that you can verify line by line), a Jane Reports tool for clinic
					clients, a redesigned Tasks page, Backoffice work on the client page and in the Tracker, a faster Engagement Tracker with CSV
					export, a long list of Bookkeeping Tool fixes, and a security fix for the Client Hub and the old client portal.
				</p>
			</header>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🤖 Playbooks</h2>
				<p className="product-release__section-body">
					Playbooks are AI checks the firm writes once and runs on a client's books every month or year end. A playbook reads the client's
					QuickBooks and records, runs the firm's own checks, and reports what it found with the evidence behind every figure. A person
					always decides what happens next. You'll find it under <strong>Playbooks</strong> in the left menu, with Results, Playbooks,
					Skills, Toolbox and Rules tabs.
				</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<h3 className="product-release__section-subheading">⚙️ How it works</h3>
				<ol className="product-release__ordered-list">
					<li>
						Build. You describe the job you want automated in a chat with the guide. The guide asks what it needs, suggests which sources
						and skills to use, and drafts the playbook. The build page fits on one screen, shows the four stages (describe, draft, test,
						publish), keeps a To-do list of what's waiting on you, and shows the real steps the guide is taking while it works.
					</li>
					<li>
						Test and publish. You test the draft on one client before anyone else can use it. A draft can't be published while one of the
						statement lines it covers has no check about it, unless someone waives that line with a reason. Once published, a playbook keeps
						the exact skill versions it was published with, so editing a skill later never silently changes a live playbook.
					</li>
					<li>
						Run. Start a playbook for a client (or many clients at once). The firm's checks run in code first — the month-end or year-end checks
						for the statement lines the playbook covers — and then the AI does its part. You can watch each step live, see how long it took,
						and what the run cost in US dollars. A run that stops part-way (for example on a server restart) can be resumed.
					</li>
					<li>
						Review. Each finding is a card with its evidence, a decision (accept, or "Change to" and "Why"), any proposed journal entry or
						Workflow action, and a sign-off. By default the client's own managers decide and their senior manager or partner signs off; a
						playbook can instead name team roles.
					</li>
				</ol>

				<h3 className="product-release__section-subheading">🔍 Verifiable review cards</h3>
				<ul className="product-release__bullet-list">
					<li>
						Every figure a finding states gets a proof line: a tick or a red mark with its label and value, and a one-line summary such as
						"7 of 7 figures found in the source", "Calculated by code" or "1 figure not in the source".
					</li>
					<li>
						"See the rows" / "Show me" opens a side panel next to the report with exactly what the agent read (frozen at run time), the
						ledger as it is now with the cited lines highlighted and anything changed since the run, the trial balance at period end, the
						transaction with its attachments, or an "Open in Google Drive" link for a document.
					</li>
					<li>A journal entry whose figures can't be proven in the source can't be booked. The card and the booking window say why.</li>
					<li>
						A finding about an account that isn't linked to a statement line says so and tells you to link it in Bookkeeping's QuickBooks
						trial balance. A run that meets such accounts stops as "Needs linking", names them, and offers Resume once they're linked.
					</li>
					<li>
						If a tool a playbook uses changed after it was published, the run shows an amber "Changed since publish" badge and asks for the
						playbook to be tested and republished.
					</li>
				</ul>

				<h3 className="product-release__section-subheading">📒 Journal entries and Workflow actions</h3>
				<ul className="product-release__bullet-list">
					<li>
						An accepted month-end journal entry can be booked into the client's QuickBooks with one click. QuickBooks is read again first:
						the accounts must still be active, the entry must balance, and the balances must be what they were when the entry was proposed.
						The card then shows the QuickBooks journal number and who booked it.
					</li>
					<li>
						A booked journal can be removed from QuickBooks from the same card. If it was changed in QuickBooks since booking, you're told
						why and asked before anything is deleted. Every re-check of the books reads booked journals back, so a card never claims a
						booking QuickBooks no longer holds.
					</li>
					<li>
						Workflow actions a playbook proposes (create or complete a task, add a note, move an engagement's stage) can be executed from
						the run report with an "Execute" button that shows exactly what will happen. Nothing happens until someone executes it. Only
						people an administrator grants the execute permission can do this; nobody has it by default.
					</li>
					<li>You can only start or test a playbook for clients you may act for: administrators, or members of the client's team.</li>
				</ul>

				<h3 className="product-release__section-subheading">🧾 Decision Records and Corrections</h3>
				<ul className="product-release__bullet-list">
					<li>
						Every judgment a playbook makes is kept as a Decision Record: what the agent saw, what it answered and what the firm finally
						decided, tied to the statement line and the check it came from. So every decision ever made about, say, HST payable can be found.
					</li>
					<li>
						Each playbook has a Corrections tab showing how often people agreed with its judgments, with a Statement line filter and an
						export for administrators.
					</li>
				</ul>

				<h3 className="product-release__section-subheading">💰 Compensation plan playbook</h3>
				<ul className="product-release__bullet-list">
					<li>
						A playbook can now ask for values when it runs (an amount, a date, a month, a choice, yes/no or text) through a short form.
					</li>
					<li>
						CRA's T4127 payroll formulas (2026 federal, Ontario and CPP) are now in code and proven against CRA's own Payroll Deductions Online
						Calculator. A payroll deductions tool gives a client's monthly salary schedule for the year with the CRA payment and due date for
						each month.
					</li>
					<li>
						The compensation plan letter comes out as a PDF and an Excel file, prints the client's address the way Workflow stores it, keeps
						the CRA remittance table on one page, and states a mid-year salary change truthfully.
					</li>
				</ul>

				<h3 className="product-release__section-subheading">💡 Worth knowing</h3>
				<ul className="product-release__bullet-list">
					<li>
						Month-end checks now report the GST/HST position separately from PST/QST, since they're owed to different governments. Six
						year-end checks that could never fire before now work: source deductions, prepaid insurance, dividends, subcontractors, WSIB and
						EHT.
					</li>
					<li>
						Financial statements now name equity for the kind of entity: Shareholders' Equity, Owner's Equity, Partners' Capital or Net
						Assets. The compilation wizard takes the entity type from what's on record for the engagement and won't run until one is chosen.
					</li>
					<li>
						When a step needs a calculation Workflow can't do yet (matching two lists, a reconciliation), the guide names it as a gap instead of
						improvising. You can build without that step or drop it. Script steps — where Workflow writes a script, an independent AI reviews
						it and a person tests and approves it — are built in but switched off until SRJ turns them on.
					</li>
					<li>Playbook runs can also produce Excel reports and presentations.</li>
				</ul>

				<div className="product-release__note">
					<span className="product-release__note-title">👀 A note on review</span>
					<p className="product-release__note-body">
						A playbook proposes; a person decides. Every finding shows where its figures came from, every journal entry is checked against
						QuickBooks before it's booked, and every Workflow action waits for someone to press Execute. This is Part 1 — editing a journal
						before booking, reclassifications and follow-ups are still being built.
					</p>
				</div>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🏥 Jane Reports tool</h2>
				<p className="product-release__section-body">
					For clinic clients who run on Jane. Under Tools, drop in a month's Jane exports and get the QuickBooks journal for that month,
					proven against Jane's own reports before it can be posted.
				</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<h3 className="product-release__section-subheading">⚙️ How it works</h3>
				<ol className="product-release__ordered-list">
					<li>
						Settings. Each client has their own journal settings: when revenue is recognised (when invoiced, or when payment is applied),
						starting from which month; where each Jane site goes (a QuickBooks location, a QuickBooks class, or neither); and whether a
						receivable is booked. Standard and Clinic Accelerator starting points fill everything in one step. Each setting shows who decided
						it and when, and past choices are kept, so an old month is always built with the settings that applied to it.
					</li>
					<li>
						Upload. Drop in the five Jane exports for the month (Billing Summary, Sales detail, Applied and Unapplied Payments, Payments,
						Accounts Receivable). The screen names any that are missing, shows what each file was checked against, and reconciles the
						reports with each other. If no revenue basis is chosen for the month, it asks for one instead of assuming.
					</li>
					<li>
						Map. Match Jane's accounts, tax codes, sites and items to QuickBooks once. Anything unmapped stops the journal with the reason.
					</li>
					<li>
						Prove and post. The journal opens with its proof: a headline saying everything ties (or naming what doesn't), a table of net
						revenue by account and location, and every check behind it. Clearing, revenue, tax, fees, refunds, the receivable and each
						location are added up from the journal's own lines and compared with what Jane states. Anything that doesn't tie blocks posting.
						The journal then posts to QuickBooks with its tax.
					</li>
				</ol>

				<h3 className="product-release__section-subheading">💡 Worth knowing</h3>
				<ul className="product-release__bullet-list">
					<li>
						The journal is shown in sections. Each heading says what the group does, which way it moves money and which Jane export it comes
						from, and each section's total shows what the clearing account holds after it. Revenue has a total under each account.
					</li>
					<li>You can see how this month's journal differs from the client's last one, and the tool says when QuickBooks already holds a Jane journal for the month.</li>
					<li>Dropping something the client's own journals record (no receivable, no location) needs a reason, shown beside who decided it.</li>
					<li>Standard and Accelerator months are checked by the same rules. Refunds and insurer/other payer receivables are handled.</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">✅ Tasks page redesign</h2>
				<p className="product-release__section-body">
					The Tasks page was rebuilt to handle the firm's full task list without freezing, and to make the common filters one click.
				</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<ul className="product-release__bullet-list">
					<li>
						Quick-filter tiles at the top (overdue, due this week, next week, later, no due date, done) and filter chips for status,
						engagement, client, group and user, with live counts. "All filters" still opens the full filter window.
					</li>
					<li>Group and sort the list the way you want, switch between list, kanban, status and deadline views, and save it as a view.</li>
					<li>Select several tasks and reassign them or change their due date in one go.</li>
					<li>
						Two default views: "Assigned to Me" (tasks others gave you) and the new "Assigned by Me" (tasks you gave others). Every task now
						shows both "Assigned To" and "Assigned By", and you can sort by either.
					</li>
					<li>
						The task window was redesigned. It shows who created the task, and "Assigned By" defaults to whoever creates it unless you pick
						someone else. The same applies to tasks added from an engagement.
					</li>
					<li>Tasks that belong to inactive engagements can be hidden.</li>
					<li>
						The list loads in pages from the server, so even a view with thousands of tasks opens quickly. Before, an unfiltered view
						could pull ten thousand rows and freeze the tab.
					</li>
					<li>The left navigation bar can be collapsed to icons to give the page more room. Workflow remembers your choice.</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🗂️ Backoffice on the client page, in the Tracker, and in Google Tasks</h2>
				<p className="product-release__section-body">
					Backoffice work (payroll, HST, bookkeeping and so on) now shows up beside workflow engagements, and preparers can see their
					backoffice tasks on their phones.
				</p>

				<div className="product-release__video product-release__video--embed">
					{/* TODO: Replace with a Loom embed, e.g. <iframe src="https://www.loom.com/embed/VIDEO_ID" title="..." frameBorder="0" allow="fullscreen" allowFullScreen /> */}
					<div className="product-release__video-placeholder">🎥 Video Guide coming soon...</div>
				</div>

				<ul className="product-release__bullet-list">
					<li>
						Client page. The Engagements tab has a Workflow / Backoffice switch. Backoffice shows the client's tasks rolled up into
						engagements, one per service and period, with a status (not started, in progress, pending client, overdue, complete). Expand a
						row to see its tasks. It's a report: nothing is edited here.
					</li>
					<li>
						Engagement Tracker. A new toggle on each tracker view: Off (default, nothing changes), Rolled up (one strip per client with the
						current and last completed engagement per service), or Rows (every backoffice engagement as its own row with its tasks). Clients
						who only have backoffice work appear too. The choice is saved with the view.
					</li>
					<li>
						Google Tasks. Backoffice tasks are copied one way into the preparer's own Google Tasks, in a "Workflow Backoffice" list, and
						updated when a task is reassigned, completed, moved to the next period or removed. Reconnect your Gmail account once in the Email
						Account window to allow it; the window shows whether it's on and has a "Retry sync" button. Each task row shows a small chip
						with its sync status, and any sync error is shown there too.
					</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">📊 Engagement Tracker: all rows, CSV export, wider search</h2>
				<ul className="product-release__bullet-list">
					<li>
						The tracker used to show only the 250 most recently updated engagements that matched your filters, silently leaving out the
						rest. It now loads everything in pages. The row count strip shows "Showing X of Y" with Load more and Load all once it pauses.
					</li>
					<li>A new Export CSV button downloads every matching row with the columns you have visible, in the order you have them.</li>
					<li>
						Search now has a Filtered / All switch. Filtered searches within your current filters; All searches every engagement and client in
						the firm.
					</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🧮 Bookkeeping Tool</h2>
				<p className="product-release__section-body">
					Fixes from three client files, plus a new way to start from QuickBooks Online or from a trial balance.
				</p>

				<h3 className="product-release__section-subheading">🟢 Start from QuickBooks Online or a trial balance</h3>
				<ul className="product-release__bullet-list">
					<li>
						A client who keeps their books in QuickBooks Online can have their statements built straight from a live pull of this year's and
						last year's trial balance. You link each QuickBooks account to the chart once (with a suggestion from its type and name), see
						whether the figures agree with QuickBooks's own profit and loss and balance sheet, and save the pull as the file's books.
					</li>
					<li>
						The year-end package lists the adjusting entries to book in QuickBooks. You can book them, or remove them, from Workflow. "The
						client entered it" looks up a journal the client already entered by its QuickBooks number, shows it beside the entry and records
						it, so nothing is counted twice.
					</li>
					<li>
						Posting or removing journals in QuickBooks is its own permission. It's off for everyone until an administrator grants it under
						Manage Permissions, which is now open to administrators only.
					</li>
					<li>
						Starting from a trial balance is now a choice on the wizard's first screen, for a client whose books are kept elsewhere and who
						has no bank statements to import. A trial balance read from a file is treated as the year's closing position, so a second year
						no longer counts every balance twice.
					</li>
					<li>Ledger entries pulled from QuickBooks that have an attachment (a receipt on a bill, say) now show it.</li>
				</ul>

				<h3 className="product-release__section-subheading">📥 Statement import</h3>
				<ul className="product-release__bullet-list">
					<li>
						Re-importing one month's statement no longer deletes the other months already booked on that account. Only that statement's
						own period is replaced.
					</li>
					<li>
						A statement re-exported from the bank with different bytes replaces the one already on the shelf for the same account and period
						instead of filing twice. A statement that carries an account number is matched to the existing bank account by that number, so
						twelve monthly statements land on one account rather than a new one each time.
					</li>
					<li>
						When a statement's account name doesn't match one on the client's record, you're asked whether it's really one of the existing
						accounts before the import continues, instead of a new account being created from the file name.
					</li>
					<li>Several Excel or CSV statements picked for the same account are merged into one statement instead of keeping only the first.</li>
					<li>
						Every account on the statements screen must name the chart account it posts to before the import continues. You can add a new
						chart account on the spot, and the same "add an account" screen is used everywhere one can be added.
					</li>
					<li>
						Importing a PDF statement reuses the reading the preview already made, so a file that previewed cleanly no longer fails or comes
						back different on import. A PDF import now files the statement on the shelf and ties its transactions to it, which also gives it
						its own tab on the General Ledger download.
					</li>
					<li>
						A statement whose signs read backwards everywhere is corrected automatically, and each corrected row keeps what the statement
						printed, with a small ± marker next to the amount.
					</li>
					<li>
						Removing a statement that never read cleanly and uploading the same file again now reads it fresh, and the screen says so,
						instead of bringing back the old incomplete result.
					</li>
					<li>
						Dates like 04/05 are read with one convention for the whole column instead of guessed row by row, so day and month no longer get
						swapped on some rows.
					</li>
					<li>
						Two genuine transactions that share a date, amount and description (recurring same-amount charges) both survive an import instead
						of one being dropped as a duplicate.
					</li>
					<li>
						Several lines on the statement reading screen can be corrected at once, each with its own draft and its own error, with a "Save
						all" button.
					</li>
					<li>
						If filing a statement fails during Generate, the books are no longer generated anyway. Generate stops with a visible error and
						nothing saved.
					</li>
				</ul>

				<h3 className="product-release__section-subheading">🏷️ Classification and transfers</h3>
				<ul className="product-release__bullet-list">
					<li>
						Industries are now a list you can add to (Holding Company was the first missing one), without waiting for a deploy. The client's
						industry now actually reaches the AI that classifies transactions, through the manual Classify button.
					</li>
					<li>
						A transaction filed to Unclassified keeps the code it was classified to, and adding that account to the chart re-ties every
						transaction that was waiting for it. Adding several new accounts from the review is one request.
					</li>
					<li>
						A transaction naming a shareholder or related party is no longer proposed as half of a transfer pair just because its amount and
						date match something else.
					</li>
					<li>
						Reclassifying one leg of a transfer now shows correctly everywhere: the Transfer badge, the Transfers tab and its count, the Review
						tab and the classification summary all read the current classification.
					</li>
					<li>A reconciliation item that can't be booked now says why, and the bulk action says how many were not recorded.</li>
				</ul>

				<h3 className="product-release__section-subheading">📘 Adjusting entries, chart of accounts and reconciliation</h3>
				<ul className="product-release__bullet-list">
					<li>
						Approving an adjusting entry can no longer wipe out every adjusting entry on the engagement. A batch is checked to balance before
						anything already saved is touched, and a failed save shows an error and rolls back instead of being silently swallowed.
					</li>
					<li>
						An adjusting entry line with no chart account, or a negative amount, is reported as not posted (with the reason) instead of being
						silently dropped from the trial balance.
					</li>
					<li>
						Working on books whose statements don't reconcile now needs two people. The request stays pending until someone other than the
						requester approves it.
					</li>
					<li>
						A chart account can be put away from Settings once nothing points at it, and brought back later. Nothing is deleted, and the payees
						remembered against it apply again when it's brought back. A custom tax rate can be entered from Settings or when adding an
						account.
					</li>
					<li>The opening balances screen says when prior-year ledger rows are not in the figures it shows, and why.</li>
					<li>A disabled Continue button now says why it's disabled.</li>
				</ul>

				<h3 className="product-release__section-subheading">📤 General Ledger download and Flip signs</h3>
				<ul className="product-release__bullet-list">
					<li>
						The download's bank and card tabs now find the booking behind every statement line, whatever its sign or currency, so the Account
						# and Account columns are filled in and the tabs can be imported into CaseWare. Foreign-currency tabs gain a CAD column, and
						every tab gains an "In the books" column (blank, "Booked the other way round" or "Not in the books") with counts in its notes.
					</li>
					<li>
						"Flip signs in the download" now also flips the bank and card tabs. Trial Balance and General Ledger stay as the books, and the
						notes say exactly which tabs flipped. Card tabs say that a positive amount is a payment or refund.
					</li>
					<li>
						Settings &gt; Flip signs now checks the statements on file first. If any statement would stop supporting the books, a warning
						shows the counts and the amount that would be left unaccounted for, with "Keep as it is" or "Flip anyway", and says how to undo a
						flip. Otherwise it flips straight away as before.
					</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">🔐 Client Hub and the old client portal</h2>
				<p className="product-release__section-body">
					A security fix that closes a staff sign-in bypass and locks down the old client portal, which clients still use, plus a new
					declaration on forms.
				</p>
				<ul className="product-release__bullet-list">
					<li>
						A crafted web address can no longer match another staff member's session, and a broken sign-in header can no longer stop the
						API.
					</li>
					<li>
						The old client portal at app.srjca.com keeps working, but every route it uses is now locked to the signed-in contact: they can
						only list, download, upload or delete their own clients' documents. A sign-in never returns a SIN, date of birth or password.
					</li>
					<li>
						Account setup and password reset links are single-use, expire in 7 days, and set only the password of the contact they were sent
						to. The email service no longer logs email bodies, so no link ever reaches the logs.
					</li>
					<li>
						Staff can only read, update or assign contacts of their own firm. A link to another firm's client adds nothing.
					</li>
					<li>
						In the contact window, the email button now says "Send Account Setup Email" or "Send Reset Password Email" depending on whether
						the contact has an account. If an email can't be sent (no address, hourly limit, or the mail service refused it) you see "The
						email could not be sent" instead of a false "Email sent".
					</li>
					<li>
						In the Client Hub, a client now says whether they're completing a form for themselves or on the client's behalf (and in what
						relationship) before it can be submitted. The declaration is recorded with who said it and when, and each change is written to the
						client's activity.
					</li>
					<li>Connecting a firm's Google Drive needs a signed-in administrator.</li>
				</ul>
			</section>

			<section className="product-release__section">
				<h2 className="product-release__section-heading">👥 Clients, contacts and engagements</h2>
				<ul className="product-release__bullet-list">
					<li>
						"Managed by" now shows in the engagements list (with its own column) and in the engagement window, and both the engagement
						filters and the client filters have a "Managed By" filter. It follows whichever manager slot the client marked.
					</li>
					<li>
						Contacts have three new fields: Secondary Email, Referred By and Other Info. The billing "Zip Code" field is now labelled
						"Postal Code / Zip Code".
					</li>
					<li>
						In the personal clients list, click the arrow beside a client to expand their sub contacts, with each one's relation, email and
						phone.
					</li>
					<li>
						The Relation column and field only show for personal clients, and the relation options now include Partner and Child. Sub contacts
						are only loaded for personal clients.
					</li>
					<li>
						The clients CSV export only includes the CRA Business Number column for corporate and trust clients, since a personal client's
						number is their SIN.
					</li>
				</ul>
			</section>

			<section className="product-release__section product-release__changelog">
				<h2 className="product-release__section-heading">🎫 Full ticket list</h2>
				<ul className="product-release__changelog-list">
					<li>W-1087 - Playbooks: AI playbooks with verifiable review cards (Part 1)</li>
					<li>W-1086 - Bookkeeping Tool: Start from QuickBooks Online and book adjusting entries into QuickBooks</li>
					<li>W-1022 - Bookkeeping Tool: trial balance start, chart account mapping and retirement</li>
					<li>W-1025 - Jane Reports: Jane exports to a proved QuickBooks journal</li>
					<li>W-1092 - Bookkeeping Tool: statement import, reconciliation and classification fixes</li>
					<li>W-1138 - General Ledger download bank tabs, and Flip signs warns first</li>
					<li>W-1110 - Tasks lists revamped</li>
					<li>W-1042 - "Assigned by" and "Assigned to" views in tasks list</li>
					<li>W-1105 - Backoffice engagements on the client page, in the Tracker, and Google Tasks sync</li>
					<li>W-1126 - Engagement Tracker paging, CSV export and search</li>
					<li>W-1119 - Client Hub security fix, old client portal locked, who is completing each form</li>
					<li>W-1094 - "Managed by" in engagements list and filters</li>
					<li>W-1095 - New fields in client contacts</li>
					<li>W-1112 - Sub contacts collapsible in the clients list</li>
					<li>W-978 - Fixes to Clients screen</li>
				</ul>
			</section>
		</>
	);
};

export default Rev36;
